import { useMemo, type CSSProperties } from "react"
import { Table } from "antd"
import type { TableColumnsType } from "antd"
import type { TileColumn, TileResult } from "@csbi/contracts"
import { csvCell, formatPeriod, formatValue, tenantColor, type ThemeMode, type ValueFormat } from "@csbi/viz-core"

export type TableViewProps = {
  result: TileResult
  mode: ThemeMode
  valueFormat?: ValueFormat
  height?: number | string
  categoryLabel?: (raw: string) => string
}

type Row = Record<string, string | number | null> & { _key: string }

const isMeasure = (c: TileColumn) => c.role === "measure"

const COLUMN_WIDTH = { time: 100, dimension: 150, count: 130, text: 190, timestamp: 170 } as const

const widthOf = (col: TileColumn): number => {
  if (col.role === "time") return COLUMN_WIDTH.time
  if (col.role === "dimension") return COLUMN_WIDTH.dimension
  if (col.unit === "text") return COLUMN_WIDTH.text
  if (col.unit === "timestamp") return COLUMN_WIDTH.timestamp
  return COLUMN_WIDTH.count
}

function TenantCell({ name, colorSlot, mode }: { name: string; colorSlot: number | undefined; mode: ThemeMode }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {colorSlot ? (
        <span
          aria-hidden="true"
          style={{
            width: 9,
            height: 9,
            borderRadius: 999,
            flex: "0 0 auto",
            background: tenantColor(colorSlot, mode),
          }}
        />
      ) : null}
      <span>{name}</span>
    </span>
  )
}

export function TableView({ result, mode, valueFormat = "auto", height, categoryLabel }: TableViewProps) {
  const slotOf = new Map(result.perTenant.map((t) => [t.tenantId, t.colorSlot]))
  const nameOf = new Map(result.perTenant.map((t) => [t.tenantId, t.displayName]))
  const granularity = result.meta.resolvedRange.granularity

  /**
   * ⚠ **A resolved dimension column can end up repeating its neighbour.**
   */
  const redundant = useMemo(() => {
    const out = new Set<string>()
    if (!categoryLabel || result.rows.length === 0) return out
    const dims = result.columns.filter((c) => c.role === "dimension" && !c.key.endsWith(".tenant_id"))
    for (const d of dims) {
      const twin = result.columns.some(
        (other) =>
          other.key !== d.key &&
          result.rows.every((r) => categoryLabel(String(r[d.key] ?? "")) === String(r[other.key] ?? "")),
      )
      if (twin) out.add(d.key)
    }
    return out
  }, [result, categoryLabel])

  const visibleColumns = result.columns.filter((c) => !redundant.has(c.key))

  const columns: TableColumnsType<Row> = visibleColumns.map((col) => {
    const numeric = isMeasure(col) && col.unit !== "text" && col.unit !== "timestamp"
    return {
      title: col.title,
      dataIndex: col.key,
      key: col.key,
      width: widthOf(col),
      align: numeric ? "right" : "left",
      ellipsis: !numeric,
      onCell: numeric ? () => ({ style: { fontVariantNumeric: "tabular-nums" } }) : undefined,
      render: (raw: string | number | null) => {
        if (col.role === "time") return typeof raw === "string" ? formatPeriod(raw, granularity) : "—"
        if (col.key.endsWith(".tenant_id")) {
          const id = String(raw ?? "")
          return <TenantCell name={nameOf.get(id) ?? id} colorSlot={slotOf.get(id)} mode={mode} />
        }
        if (!isMeasure(col)) {
          if (raw == null || raw === "") return "(uncategorised)"
          return categoryLabel?.(String(raw)) ?? String(raw)
        }

        const value = numeric && typeof raw === "string" ? Number(raw) : raw

        // Missing is —, never 0. That invariant matters as much in a table as on a chart
        return formatValue(value, {
          valueType: col.unit ?? "count",
          format: valueFormat,
          context: "table",
        })
      },
    }
  })

  const rows: Row[] = result.rows.map((r, i) => ({ ...r, _key: String(i) }))
  const minWidth = visibleColumns.reduce((sum, c) => sum + widthOf(c), 0)

  return (
    <div
      className="csbi-table-scroll"
      style={{ height: height ?? "100%", "--csbi-table-min": `${minWidth}px` } as CSSProperties}
    >
      <Table<Row>
        size="small"
        rowKey="_key"
        columns={columns}
        dataSource={rows}
        pagination={rows.length > 20 ? { pageSize: 20, size: "small", showSizeChanger: false } : false}
        locale={{ emptyText: "No data" }}
      />
    </div>
  )
}

/** The table view and the CSV export share one set of columns and rows, so "what you see" and "what you export" cannot differ */
export function toCsv(result: TileResult): string {
  const header = result.columns.map((c) => csvCell(c.title)).join(",")
  const body = result.rows.map((r) => result.columns.map((c) => csvCell(r[c.key])).join(","))
  return [header, ...body].join("\n")
}
